<nav
  class="sidemenu"
  [ngClass]="{
    'sidemenu--expanded': menuExpandedState(),
    'sidemenu--collapsed': !menuExpandedState(),
  }"
>
  <!-- Screen reader announcement for menu state -->
  <div class="sr-only" role="status" aria-live="polite" aria-atomic="true">
    {{ srAnnouncement() }}
  </div>

  <div class="sidemenu__top-section" [class.collapsed]="!menuExpandedState()">
    <div
      class="sidemenu__top-section__user-section"
      (click)="openUserMenu($event)"
      (keydown.enter)="openUserMenu($event)"
      (keydown.space)="openUserMenu($event)"
      [tabindex]="0"
      role="button"
      [attr.aria-label]="'User menu'"
      [attr.aria-expanded]="!!overlayRef"
    >
      <div class="sidemenu__top-section__user-section__avatar-container">
        <img
          [src]="user().image"
          alt="User"
          class="sidemenu__top-section__user-section__avatar-container__avatar"
        />
        <div class="sidemenu__top-section__user-section__avatar-container__status-indicator"></div>
      </div>
      @if (menuExpandedState()) {
        <span class="sidemenu__top-section__user-section__username">{{ user().name }}</span>
      }
      <button type="button" class="sidemenu__top-section__user-section__chevron" tabindex="-1">
        <palantir-icon name="chevron-down-outline"></palantir-icon>
      </button>
    </div>

    @if (showRocketButton()) {
      <button
        type="button"
        class="sidemenu__top-section__rocket-button"
        [attr.aria-label]="'Launch Rocket'"
        [class.collapsed]="!menuExpandedState()"
        (click)="onRocketButtonClick()"
        (keydown.enter)="onRocketButtonClick()"
        (keydown.space)="onRocketButtonClick()"
      >
        <palantir-icon name="rocket-launch-outline"></palantir-icon>
        @if (hasCompanyNews()) {
          <div class="sidemenu__top-section__rocket-button__indicator"></div>
        }
      </button>
    }
  </div>

  <ul
    class="sidemenu__menu-items palantir-scrollbar"
    role="menubar"
    [attr.aria-label]="'Main navigation'"
  >
    @for (item of menuItems(); track item.id; let i = $index) {
      @if (!item.hidden && !areAllChildrenHidden(item)) {
        @if (item.withDivider) {
          <li class="menu-divider" role="separator"></li>
        }
        <li
          #menuItemElement
          (click)="!menuExpandedState() && openClickMenu($event, item)"
          (keydown)="onMenuKeyDown($event, i)"
          (keydown.enter)="!menuExpandedState() && openClickMenu($event, item)"
          (keydown.space)="!menuExpandedState() && openClickMenu($event, item)"
          [class.has-children]="hasChildrenSafe(item)"
          [class.expanded]="isSubmenuExpanded(item.title)"
          [class.active-route]="isActiveRoute(item)"
          [tabindex]="!menuExpandedState() ? 0 : -1"
          role="menuitem"
          [attr.aria-label]="'Menu item: ' + item.title"
          [attr.aria-haspopup]="hasChildrenSafe(item) ? 'menu' : null"
          [attr.aria-expanded]="hasChildrenSafe(item) ? isSubmenuExpanded(item.title) : null"
          [palantirTooltip]="item.title"
          palantirTooltipPosition="right"
          [palantirTooltipDisabled]="menuExpandedState()"
        >
          <a
            class="sidemenu__menu-items__item"
            (click)="item.children ? toggleSubmenu(item.title) : item.url && navigateToRoute(item)"
            (keydown.enter)="
              item.children ? toggleSubmenu(item.title) : item.url && navigateToRoute(item)
            "
            (keydown.space)="
              item.children ? toggleSubmenu(item.title) : item.url && navigateToRoute(item)
            "
            [tabindex]="item.url || item.children ? 0 : -1"
            [class.active]="selectedMenuItem()?.id === item.id || isActiveRoute(item)"
            role="button"
            [attr.aria-label]="item.title"
          >
            <palantir-icon
              [name]="getMenuItemIcon(item)"
              class="sidemenu__menu-items__item__icon"
            ></palantir-icon>
            <span
              [class.collapsed]="!menuExpandedState()"
              class="sidemenu__menu-items__item__label"
              >{{ item.title }}</span
            >
            @if (item.badge && menuExpandedState()) {
              <span
                class="menu-badge"
                [style.background-color]="item.badge.bg"
                [attr.aria-label]="'Badge: ' + item.badge.title"
              >
                {{ item.badge.title }}
              </span>
            }
            @if (hasChildrenSafe(item) && menuExpandedState()) {
              <palantir-icon
                [class.collapsed]="!menuExpandedState()"
                [class.expanded]="isSubmenuExpanded(item.title)"
                name="chevron-down-outline"
                class="submenu-arrow"
              ></palantir-icon>
            }
          </a>

          @if (hasChildrenSafe(item) && menuExpandedState()) {
            <ul
              class="submenu"
              [class.submenu--expanded]="isSubmenuExpanded(item.title)"
              role="menu"
              [attr.aria-label]="item.title + ' submenu'"
            >
              @for (child of item.children; track child.id) {
                @if (!child.hidden) {
                  <li
                    (click)="child.url && navigateToRoute(child)"
                    (keydown.enter)="child.url && navigateToRoute(child)"
                    (keydown.space)="child.url && navigateToRoute(child)"
                    [tabindex]="child.url ? 0 : -1"
                    [attr.aria-label]="child.title"
                    [class.active]="selectedMenuItem()?.id === child.id || isActiveRoute(child)"
                    role="menuitem"
                  >
                    <palantir-icon
                      [name]="getChildItemIcon(child)"
                      class="sidemenu__menu-items__item__icon"
                    ></palantir-icon>
                    <span>{{ child.title }}</span>
                    @if (child.badge) {
                      <span
                        class="menu-badge menu-badge--small"
                        [style.background-color]="child.badge.bg"
                        [attr.aria-label]="'Badge: ' + child.badge.title"
                      >
                        {{ child.badge.title }}
                      </span>
                    }
                  </li>
                }
              }
            </ul>
          }
        </li>
      }
    }
  </ul>

  <div class="sidemenu__footer" [class.sidemenu__footer--collapsed]="!menuExpandedState()">
    <div class="sidemenu__footer__company">
      <img
        [src]="companyImage()"
        alt="Company Logo"
        class="sidemenu__footer__company__logo"
        [class.sidemenu__footer__company__logo--full]="!company().name"
      />
      @if (menuExpandedState() && company().name) {
        <span class="sidemenu__footer__company__name">{{ company().name }}</span>
      }
    </div>

    <div
      class="sidemenu__footer__actions"
      [class.sidemenu__footer__actions--collapsed]="!menuExpandedState()"
    >
      <button
        type="button"
        (click)="onHelpButtonClick()"
        (keydown.enter)="onHelpButtonClick()"
        (keydown.space)="onHelpButtonClick()"
        [attr.aria-label]="'Help'"
      >
        <palantir-icon name="help-outline"></palantir-icon>
      </button>

      <button
        type="button"
        class="sidemenu__footer__actions__item"
        (click)="onNotificationButtonClick()"
        (keydown.enter)="onNotificationButtonClick()"
        (keydown.space)="onNotificationButtonClick()"
        [attr.aria-label]="
          'Notifications' + (notificationCount() > 0 ? ', ' + notificationCount() + ' unread' : '')
        "
      >
        @if (notificationCount() > 0) {
          <div class="sidemenu__footer__actions__item__indicator">
            @if (notificationCount() > 9) {
              9+
            } @else {
              {{ notificationCount() }}
            }
          </div>
        }
        <palantir-icon name="notifications-outline"></palantir-icon>
      </button>

      <button
        type="button"
        class="sidemenu__footer__actions__toggle"
        (click)="toggleMenu()"
        [class.expanded]="menuExpandedState()"
        [attr.aria-label]="menuExpandedState() ? 'Recolher menu' : 'Expandir menu'"
        [attr.aria-expanded]="menuExpandedState()"
      >
        <palantir-icon name="start-arrow-outline"></palantir-icon>
      </button>
    </div>
  </div>
</nav>

<ng-template #clickMenuTemplate>
  <div class="click-menu" role="menu" [attr.aria-label]="selectedMenuItem()?.title + ' submenu'">
    @if (childrenItemsToShowOnCollapsedMenu().length && selectedMenuItem()) {
      <div class="click-menu__header">
        <span class="click-menu__header__title">{{ selectedMenuItem()?.title }}</span>
      </div>
    }

    @if (childrenItemsToShowOnCollapsedMenu()?.length) {
      <ul class="click-menu__items">
        @for (child of childrenItemsToShowOnCollapsedMenu(); track child.id) {
          @if (!child.hidden) {
            <li
              class="click-menu__items__item"
              [class.active]="selectedMenuItem()?.id === child.id || isActiveRoute(child)"
              (click)="child.url && navigateToRoute(child)"
              (keydown.enter)="child.url && navigateToRoute(child)"
              (keydown.space)="child.url && navigateToRoute(child)"
              [tabindex]="child.url ? 0 : -1"
              role="menuitem"
              [attr.aria-label]="child.title"
            >
              <palantir-icon
                [name]="getChildItemIcon(child)"
                class="sidemenu__menu-items__item__icon"
              ></palantir-icon>
              <span class="click-menu__items__item__label">{{ child.title }}</span>
              @if (child.badge) {
                <span
                  class="menu-badge menu-badge--small"
                  [style.background-color]="child.badge.bg"
                  [attr.aria-label]="'Badge: ' + child.badge.title"
                >
                  {{ child.badge.title }}
                </span>
              }
            </li>
          }
        }
      </ul>
    }
  </div>
</ng-template>

<ng-template #clickUserTemplate>
  <div class="click-user-menu" role="menu" [attr.aria-label]="'User menu'">
    <div class="click-user-menu__header">
      <div class="click-user-menu__header__avatar-container">
        <img [src]="user().image" alt="User Avatar" class="click-user-menu__header__avatar" />
        <div class="click-user-menu__header__status-indicator"></div>
      </div>

      <div class="click-user-menu__header__info">
        <span class="click-user-menu__header__name">{{ user().name }}</span>
        <span class="click-user-menu__header__email">{{ user().email }}</span>
      </div>
    </div>

    <div class="click-user-menu__actions">
      <button
        type="button"
        class="click-user-menu__actions__item"
        (click)="handleUserAction('profile')"
        (keydown.enter)="handleUserAction('profile')"
        (keydown.space)="handleUserAction('profile')"
        [tabindex]="0"
        role="menuitem"
        [attr.aria-label]="'Gerenciar perfil'"
      >
        <palantir-icon
          name="settings-outline"
          class="click-user-menu__actions__item__icon"
        ></palantir-icon>
        <span class="click-user-menu__actions__item__label">Gerenciar perfil</span>
      </button>
    </div>

    <div class="click-user-menu__divider"></div>

    <button
      type="button"
      class="click-user-menu__actions__item--logout"
      (click)="handleUserAction('logout')"
      (keydown.enter)="handleUserAction('logout')"
      (keydown.space)="handleUserAction('logout')"
      [tabindex]="0"
      role="menuitem"
      [attr.aria-label]="'Sair'"
    >
      <palantir-icon
        name="logout-outline"
        class="click-user-menu__actions__item__icon"
      ></palantir-icon>
      <span class="click-user-menu__actions__item__label">Sair</span>
    </button>
  </div>
</ng-template>
